import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThemeProvider, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import CheckoutTelrCard from "/imports/ui/views/CheckoutTelrCard";
import CardDeclinedTip from "/imports/ui/components/shared/CardDeclinedTip";
import { capturesToday, formatDeadline, holdCardData, holdDeadline } from "/imports/ui/views/CheckoutTelrHoldView";

// Telr can bounce back before its own status settles ("PENDING" outcome on the
// /done route) — poll a few times before declaring it still-processing.
const MAX_STATUS_POLLS = 5;
const POLL_INTERVAL_MS = 3000;

export default function CheckoutTelrHoldDoneView() {
  const { holdToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const widgetSlug = sessionStorage.getItem("widget_slug");

  const isCancelledRoute = location.pathname.endsWith("/cancelled");
  const isDeclinedRoute = location.pathname.endsWith("/declined");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [outcome, setOutcome] = useState(null); // "CANCELLED" | "DECLINED" | "PENDING" | null
  const [telrDeclined, setTelrDeclined] = useState(false);
  const pollCount = useRef(0);

  const theme = useMemo(() => createWidgetTheme({ primaryColor: data?.widgetColor || undefined }), [data?.widgetColor]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const check = async () => {
      const res = await api.checkTelrHoldStatus(holdToken).catch(() => null);
      if (cancelled) return;
      const result = res?.result || {};

      if (result.authorized) {
        // Re-fetch so the success pane shows the authoritative captureDueAt
        // (computed server-side when the hold settled).
        const fresh = await api.getTelrHold(holdToken).catch(() => null);
        if (cancelled) return;
        if (fresh) setData(fresh);
        setAuthorized(true);
        setOutcome(null);
        setLoading(false);
        return;
      }

      // Only the success route keeps polling — Telr said no on the others.
      const stillPending = !result.outcome || result.outcome === "PENDING";
      if (stillPending && !isCancelledRoute && !isDeclinedRoute && pollCount.current < MAX_STATUS_POLLS) {
        pollCount.current += 1;
        timer = setTimeout(check, POLL_INTERVAL_MS);
        return;
      }

      setOutcome(result.outcome || (isCancelledRoute ? "CANCELLED" : isDeclinedRoute ? "DECLINED" : "PENDING"));
      setTelrDeclined(result.outcome === "DECLINED");
      setLoading(false);
    };

    (async () => {
      try {
        const res = await api.getTelrHold(holdToken);
        if (cancelled) return;
        setData(res);
        applyBranding({ name: res?.saas?.name, logoUrl: res?.saas?.logoUrl });
      } catch (_err) {
        if (!cancelled) setLoading(false);
        return;
      }
      await check();
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [holdToken, isCancelledRoute, isDeclinedRoute]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const game = data?.game;
  const gameUrl = widgetSlug && game?.bookishId ? `/${widgetSlug}/bookings/${game.bookishId}` : null;
  const retryHold = () => navigate(`/checkout/telr-hold/${holdToken}`);

  // Only when Telr itself reported the decline — not the route fallback used when
  // the status check failed. Left column on desktop, stacked flow on a phone.
  const isDeclined = !!game && !authorized && outcome === "DECLINED" && telrDeclined;
  const desktopDeclinedTip = isDeclined ? <CardDeclinedTip clubName={data?.saas?.name} sx={{ mb: 0 }} /> : null;

  let rightPaneContent;
  if (!game) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          This card-hold link is no longer active
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please contact the venue if you need assistance.
        </Typography>
      </Box>
    );
  } else if (authorized) {
    rightPaneContent = (
      <Box textAlign="center">
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "5rem",
            height: "5rem",
            borderRadius: "50%",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            mb: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: "2.75rem", color: "primary.main" }} />
        </Box>
        <Typography variant="h6" fontWeight={600} mb={1}>
          Card hold confirmed
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {capturesToday(holdDeadline(game, data?.saas?.timeZone))
            ? "Your court is secured. Share your booking link so friends can claim and pay for their spots."
            : "Your court is secured — no charge was made today. Share your booking link so friends can claim and pay for their spots."}{" "}
          Unpaid spots will be charged to your card {formatDeadline(holdDeadline(game, data?.saas?.timeZone))}.
        </Typography>
        {gameUrl ? (
          <Button variant="contained" fullWidth onClick={() => navigate(gameUrl, { replace: true })}>
            View game & share link
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You can now close this tab.
          </Typography>
        )}
      </Box>
    );
  } else if (outcome === "PENDING") {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" mb={1}>
          Your card hold is being processed
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          We're waiting for final confirmation from the payment provider. This can take a few moments — please don't authorize again. Your booking
          page will update once it's confirmed.
        </Typography>
        {gameUrl && (
          <Button variant="contained" fullWidth onClick={() => navigate(gameUrl, { replace: true })}>
            View booking
          </Button>
        )}
      </Box>
    );
  } else if (outcome === "CANCELLED") {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CancelIcon sx={{ fontSize: "4.5rem", color: "error.main", mb: 2 }} />
        <Typography variant="h6">Card hold was cancelled</Typography>
        <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
          No hold was placed on your card. Your court isn't secured yet — authorize the hold to keep the booking, or it will be released shortly.
        </Typography>
        <Button variant="contained" fullWidth onClick={retryHold}>
          Try again
        </Button>
      </Box>
    );
  } else {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CancelIcon sx={{ fontSize: "4.5rem", color: "error.main", mb: 2 }} />
        <Typography variant="h6" mb={1}>
          Card hold was declined
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={isDeclined ? { xs: 2, md: 3 } : 3}>
          Your court isn't secured yet — try again with a different card, or the booking will be released shortly.
        </Typography>
        {isDeclined && (
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <CardDeclinedTip clubName={data?.saas?.name} />
          </Box>
        )}
        <Button variant="contained" fullWidth onClick={retryHold}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CheckoutTelrCard data={holdCardData(data)} rightPaneContent={rightPaneContent} leftPaneContent={desktopDeclinedTip} />
    </ThemeProvider>
  );
}
